// 🚫 Advanced 404 Not Found Page — copy from homepage_sections.slug = not_found (config.admin)

import React, { memo, useEffect, useState } from 'react';
import { useAdminNavigate } from '../navigation/useAdminNavigate';
import ApiService from '../services/api';

const DEFAULT_ADMIN = {
  headline: 'Oops! Page Not Found',
  subtitle: "The page you're looking for seems to have taken a wrong turn.",
  tagline: "Don't worry, even our best drivers sometimes miss a turn!",
  goHomeLabel: 'Go Back Home',
  goBackLabel: 'Go Back',
  quickLinksTitle: 'Quick Links',
  quickLinks: [
    { menuId: 'dashboard', title: 'Dashboard', subtitle: 'Overview', icon: 'fas fa-tachometer-alt', color: 'blue' },
    { menuId: 'bookings', title: 'Bookings', subtitle: 'Manage trips', icon: 'fas fa-calendar-check', color: 'green' },
    { menuId: 'vendors', title: 'Vendors', subtitle: 'Driver partners', icon: 'fas fa-store', color: 'purple' },
    { menuId: 'analytics', title: 'Analytics', subtitle: 'Reports', icon: 'fas fa-chart-line', color: 'yellow' },
  ],
  helpPrompt: "Still can't find what you're looking for?",
  contactSupportLabel: 'Contact Support',
};

const COLOR_BG = {
  blue: 'bg-blue-100',
  green: 'bg-green-100',
  purple: 'bg-purple-100',
  yellow: 'bg-yellow-100',
};
const COLOR_TEXT = {
  blue: 'text-blue-600',
  green: 'text-green-600',
  purple: 'text-purple-600',
  yellow: 'text-yellow-600',
};

function mergeAdminConfig(raw) {
  const cfg = raw && typeof raw === 'object' ? raw : {};
  const d = DEFAULT_ADMIN;
  const links = Array.isArray(cfg.quickLinks) ? cfg.quickLinks : null;
  return {
    headline: typeof cfg.headline === 'string' && cfg.headline.trim() ? cfg.headline : d.headline,
    subtitle: typeof cfg.subtitle === 'string' && cfg.subtitle.trim() ? cfg.subtitle : d.subtitle,
    tagline: typeof cfg.tagline === 'string' && cfg.tagline.trim() ? cfg.tagline : d.tagline,
    goHomeLabel: typeof cfg.goHomeLabel === 'string' && cfg.goHomeLabel.trim() ? cfg.goHomeLabel : d.goHomeLabel,
    goBackLabel: typeof cfg.goBackLabel === 'string' && cfg.goBackLabel.trim() ? cfg.goBackLabel : d.goBackLabel,
    quickLinksTitle:
      typeof cfg.quickLinksTitle === 'string' && cfg.quickLinksTitle.trim()
        ? cfg.quickLinksTitle
        : d.quickLinksTitle,
    quickLinks: links
      ? links
          .filter((l) => l && typeof l === 'object' && l.title)
          .map((l) => ({
            menuId: String(l.menuId || ''),
            title: String(l.title),
            subtitle: String(l.subtitle || ''),
            icon: String(l.icon || 'fas fa-link'),
            color: String(l.color || 'blue'),
          }))
      : d.quickLinks,
    helpPrompt: typeof cfg.helpPrompt === 'string' && cfg.helpPrompt.trim() ? cfg.helpPrompt : d.helpPrompt,
    contactSupportLabel:
      typeof cfg.contactSupportLabel === 'string' && cfg.contactSupportLabel.trim()
        ? cfg.contactSupportLabel
        : d.contactSupportLabel,
  };
}

const NotFoundPage = memo(() => {
  const { goToMenu } = useAdminNavigate();
  const [copy, setCopy] = useState(DEFAULT_ADMIN);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await ApiService.getHomepageSectionAdmin('not_found');
        const adminCfg = res?.data?.config?.admin;
        if (!cancelled) setCopy(mergeAdminConfig(adminCfg));
      } catch {
        if (!cancelled) setCopy(DEFAULT_ADMIN);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleGoHome = () => {
    goToMenu('dashboard');
  };

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="max-w-lg w-full text-center">
        <div className="mb-8">
          <div className="relative">
            <h1 className="text-9xl font-bold text-gray-200 select-none">404</h1>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-32 h-32 bg-blue-100 rounded-full opacity-20 animate-pulse"></div>
            </div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
                <i className="fas fa-car text-white text-2xl"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{copy.headline}</h2>
          <p className="text-gray-600 text-lg mb-2">{copy.subtitle}</p>
          <p className="text-gray-500">{copy.tagline}</p>
        </div>

        <div className="space-y-4 mb-8">
          <button
            type="button"
            onClick={handleGoHome}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <i className="fas fa-home"></i>
            <span>{copy.goHomeLabel}</span>
          </button>

          <button
            type="button"
            onClick={handleGoBack}
            className="w-full border border-gray-300 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <i className="fas fa-arrow-left"></i>
            <span>{copy.goBackLabel}</span>
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{copy.quickLinksTitle}</h3>
          <div className="grid grid-cols-2 gap-4">
            {copy.quickLinks.map((link) => {
              const bg = COLOR_BG[link.color] || COLOR_BG.blue;
              const text = COLOR_TEXT[link.color] || COLOR_TEXT.blue;
              return (
                <button
                  key={`${link.menuId}-${link.title}`}
                  type="button"
                  onClick={() => link.menuId && goToMenu(link.menuId)}
                  className="text-left p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors duration-200"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 ${bg} rounded-full flex items-center justify-center`}>
                      <i className={`${link.icon} ${text}`}></i>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{link.title}</div>
                      <div className="text-sm text-gray-500">{link.subtitle}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm mb-4">{copy.helpPrompt}</p>
          <button type="button" className="text-blue-600 hover:text-blue-800 font-medium text-sm">
            {copy.contactSupportLabel}
          </button>
        </div>

        <div className="absolute top-10 left-10 w-6 h-6 bg-blue-200 rounded-full opacity-20 animate-bounce"></div>
        <div className="absolute top-20 right-20 w-4 h-4 bg-purple-200 rounded-full opacity-30 animate-pulse"></div>
        <div
          className="absolute bottom-20 left-20 w-8 h-8 bg-green-200 rounded-full opacity-20 animate-bounce"
          style={{ animationDelay: '0.5s' }}
        ></div>
        <div
          className="absolute bottom-10 right-10 w-5 h-5 bg-yellow-200 rounded-full opacity-25 animate-pulse"
          style={{ animationDelay: '1s' }}
        ></div>
      </div>
    </div>
  );
});

NotFoundPage.displayName = 'NotFoundPage';

export default NotFoundPage;
